"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NOTIFICATION_MODULES = exports.MODULE_EVENTS = exports.saveModulesSchema = exports.DELEGATION_MODULES = exports.ALL_MODULES_ON = exports.DEFAULT_MODULE_STATE = exports.CORE_MODULE_LABELS = exports.MODULE_DEFINITIONS = exports.MODULES_SETTING_KEY = exports.SWITCHABLE_MODULES = void 0;
exports.moduleDefinition = moduleDefinition;
exports.isModuleKey = isModuleKey;
exports.perModule = perModule;
exports.moduleStateOf = moduleStateOf;
exports.isModuleOn = isModuleOn;
exports.isDelegationOn = isDelegationOn;
exports.moduleStateProblem = moduleStateProblem;
exports.needsInWords = needsInWords;
exports.planModuleChange = planModuleChange;
exports.planSwitchOnWith = planSwitchOnWith;
exports.moduleDependents = moduleDependents;
exports.planRevert = planRevert;
exports.isNotificationOn = isNotificationOn;
exports.notificationKeysOff = notificationKeysOff;
const zod_1 = require("zod");
// §L34 — the module catalogue, as data, and §L28's rules for switching modules on and off.
//
// One registry read by the API (the write guard, the notification engine, the background
// jobs) and by the web app (the module bar, the palette, every <ModuleGate>), so a switch
// can never hide a screen while its emails keep going, or the other way round. The Admin
// switches them on ADMIN-17; the state is the `modules` key in `system_settings`
// (D17, ADR 0036).
/** The switchable modules, in ADMIN-17's order: on by default first, then off by default. */
exports.SWITCHABLE_MODULES = [
    'assessments',
    'templates',
    'compare',
    'cv_bank',
    'screening_order',
    'organisation',
    'requisitions',
    'approvals',
    'sla',
    'time_metrics',
    'reports',
    'workspace',
    'career_site',
    'pools',
    'offers',
    'exits',
    'exam_sessions',
    'money',
    'custom_fields',
    'inbound_mail',
    'interview_kits',
    'tasks',
    'screening_criteria',
    'jev',
    'job_image',
    'manager_shortlist',
];
exports.MODULES_SETTING_KEY = 'modules';
exports.MODULE_DEFINITIONS = [
    {
        key: 'assessments',
        label: 'Online assessments',
        description: 'Tests candidates take from a link, the question bank and marking.',
        hides: 'The Assessments menu, the Assessment stage and tab, and "Send test".',
        isOnByDefault: true,
    },
    {
        key: 'templates',
        label: 'Job templates',
        description: 'Saved job setups to start new jobs from, and interview plan templates.',
        hides: 'Role templates, interview plans and "Save as template".',
        isOnByDefault: true,
    },
    {
        key: 'compare',
        label: 'Compare',
        description: 'Finalists side by side after the interview rounds, and holds.',
        hides: 'The Compare tab on jobs, and hold reminders.',
        isOnByDefault: true,
    },
    {
        // ADR 0046: the boss asked for it, so it starts on. It does not need Talent pools.
        key: 'cv_bank',
        label: 'CV Bank',
        description: 'Good CVs kept for later, in one place, until someone removes them.',
        hides: 'The CV Bank page, "Add to CV Bank" and the tick in the reject box.',
        isOnByDefault: true,
    },
    {
        // D91 (owner, DECISIONS-LOG 2026-11-03): on so the fix is active now; a toggle in case it's ever too strict.
        key: 'screening_order',
        label: 'Screening order enforced',
        description: "Recruiters can't Advance or Move someone past Screening until Review, Shortlist and Contact have finished; HR may still send one on early, with a reason.",
        hides: "Nothing hides. Off, recruiters can Advance or Move an application past Screening before Review, Shortlist and Contact are done, as before this fix.",
        isOnByDefault: true,
    },
    {
        key: 'organisation',
        label: 'Organisation',
        description: 'The org tree, positions, the headcount plan and salary bands.',
        hides: 'The Organisation menu, salary bands and the department level of the scope switcher.',
        isOnByDefault: false,
    },
    {
        key: 'requisitions',
        label: 'Hire requests',
        description: 'Managers ask for a hire, and HR turns the request into a job.',
        hides: 'The Requisitions menu, Request a hire, starting a job from a request, and, while Offers is off too, delegation.',
        isOnByDefault: false,
        needs: ['organisation'],
    },
    {
        key: 'approvals',
        label: 'Approval chains',
        // Admin redesign 2026-11-02 (§L41): approvals run without this; it adds a venture's or department's own chain after HR's check.
        description: "Your own approvers for hire requests: after HR's check, a matching chain's steps replace Leadership.",
        hides: "Hire-request approval chains. Every request then goes to HR's check, then Leadership. Offer chains stay with Offers.",
        isOnByDefault: false,
        needsOneOf: ['requisitions', 'offers'],
    },
    {
        key: 'sla',
        label: 'Time limits',
        description: 'Working-time limits on each step, with warnings and escalations.',
        hides: 'Deadlines, breach warnings, escalation emails and stage time limits.',
        isOnByDefault: false,
    },
    {
        key: 'time_metrics',
        label: 'Time figures',
        description: 'Time to fill, time to hire and days in stage.',
        hides: 'Every time to fill, time to hire and days-in-stage figure.',
        isOnByDefault: false,
    },
    {
        key: 'reports',
        label: 'Reports and dashboards',
        description: 'The command center, proficiency, scoreboards, drop-off and the report builder.',
        hides: 'The Reports menu, funnels, source effectiveness and the morning emails.',
        isOnByDefault: false,
    },
    {
        key: 'workspace',
        label: 'Recruiter dashboard',
        description: "A recruiter's daily home page and its 8am email.",
        hides: 'The dashboard home page. Home becomes the job list.',
        isOnByDefault: false,
    },
    {
        key: 'career_site',
        label: 'Career site and postings',
        description: 'The public jobs page, postings, the application form and referrals.',
        hides: 'Postings, the application form, knockout questions and referral links.',
        isOnByDefault: false,
    },
    {
        key: 'pools',
        label: 'Talent pools',
        description: 'Groups of candidates kept for later jobs.',
        hides: 'Talent pools, "Pool" in sorting and bulk actions, and pool matches.',
        isOnByDefault: false,
    },
    {
        key: 'offers',
        label: 'Offers and hiring',
        description: 'Offer letters, offer approval, joining documents and confirming a hire.',
        hides: 'The Offers tab, offer letters, the offer step in job setup, and, while Hire requests is off too, delegation.',
        isOnByDefault: false,
    },
    {
        key: 'exits',
        label: 'Exits and backfill',
        description: 'Recording someone leaving, and drafting their replacement.',
        hides: 'Record exit and backfill.',
        isOnByDefault: false,
        needs: ['organisation'],
    },
    {
        key: 'exam_sessions',
        label: 'Exam sessions on paper',
        description: 'Tests sat on paper at a venue, with invigilators.',
        hides: 'Exam sessions, invigilators and "Book exam".',
        isOnByDefault: false,
        needs: ['assessments'],
    },
    {
        key: 'money',
        label: 'Spend and exchange rates',
        description: 'Posting spend, cost per hire and the exchange-rate table.',
        hides: 'Spend, cost per hire and exchange rates.',
        isOnByDefault: false,
    },
    {
        key: 'custom_fields',
        label: 'Custom fields',
        description: 'Extra fields of your own on candidates, jobs and requests.',
        hides: 'The custom field builder and the "More details" sections.',
        isOnByDefault: false,
    },
    {
        key: 'inbound_mail',
        label: 'Inbound email',
        description: 'Reading candidate replies from the shared mailbox.',
        hides: 'The inbound mailbox and its unassigned queue.',
        isOnByDefault: false,
    },
    {
        key: 'interview_kits',
        label: 'Interview kits',
        description: 'Question guides for interviewers.',
        hides: 'Interview kits and the kit beside the scorecard.',
        isOnByDefault: false,
    },
    {
        key: 'tasks',
        label: 'Tasks and follow-ups',
        description: 'Follow-up tasks from logged calls and emails, with reminders.',
        hides: 'My tasks, task reminders and the "next action" a logged call asks for.',
        isOnByDefault: false,
    },
    {
        // ADR 0062: the ideal candidate profile ranks every job; criteria are the older, optional lens.
        key: 'screening_criteria',
        label: 'Screening criteria',
        description: "The older way to rank: a list of criteria per job, checked against the import's columns and the CV's words. Every job now ranks by its ideal candidate.",
        hides: 'The Screening criteria section in job setup and templates, the saved criteria library, the criteria columns, Focus and ✓/✗ on Ranking, and the coverage check on Import.',
        isOnByDefault: false,
    },
    {
        // ADR 0050: nothing leaves our server until a Jev key is set (D19, reopened). ADR 0062: ranking by the
        // ideal candidate is core and no longer sits behind this switch; it covers ad-hoc asks only.
        key: 'jev',
        label: 'Ask Jev',
        description: 'The Jev search tab on Automated ranking: one-off searches in plain words, and hot filters Jev picks.',
        hides: 'The Jev search tab, with its Ask Jev box, Jev match column and order, and the hot filters Jev picks. ICP match, the ranking by the ideal candidate, is not affected.',
        isOnByDefault: false,
    },
    {
        // ADR 0064: the owner asked for it to be on whenever Jev is (2026-10-27).
        key: 'job_image',
        label: 'Job post image',
        description: "Makes each job's advert image with Google's Nano Banana 2 when the job opens, ready to post on Facebook and LinkedIn.",
        hides: 'The Make image button on the advert image, and making one when a job opens. Images already made stay.',
        isOnByDefault: false,
        comesOnWith: 'jev',
    },
    {
        // Owner 2026-11-01 (ADR 0082): HR's shortlist goes straight on to Contact unless this is on.
        key: 'manager_shortlist',
        label: 'Shortlist to the line manager',
        description: "HR sends each job's shortlist to its requester and line manager, who say yes or no to each person before anyone is contacted.",
        hides: "The Send shortlist button, the manager's answers on Review and Shortlist, the manager's shortlist page and its emails. HR moves people from Shortlist to Contact without waiting. Answers already given stay.",
        isOnByDefault: false,
    },
];
/** The modules that are always on (§L34 "Core", all ten, in §L34's order), for ADMIN-17 to list without a switch. */
exports.CORE_MODULE_LABELS = [
    { label: 'Jobs', description: 'The job list, each job and its setup, and closing a job.' },
    { label: 'Candidates', description: 'Candidates, their applications, search and duplicates.' },
    { label: 'Import', description: 'Bringing applicants in from a CSV or Excel file, with the columns matched.' },
    { label: 'Ranking', description: 'Every job ranked by its ideal candidate, Jev reading the CVs, and the two spending limits below.' },
    { label: 'HR review', description: 'Sorting mode, the pipeline list and board, and bulk actions.' },
    { label: 'Shortlist', description: "HR's shortlist for each job, and the people waiting on someone's answer." },
    { label: 'Contact', description: 'The activity timeline, messages and message templates.' },
    { label: 'Interviews', description: 'Scheduling, the calendar, scorecards and round decisions.' },
    { label: 'Platform', description: 'Notifications, people and roles, reasons, rubrics, sources, working hours, system settings and the logs.' },
    { label: 'Retention', description: 'Anonymising candidates on time. Never switched off: it protects their data.' },
];
const BY_KEY = new Map(exports.MODULE_DEFINITIONS.map((definition) => [definition.key, definition]));
function moduleDefinition(key) {
    const definition = BY_KEY.get(key);
    if (!definition)
        throw new Error(`"${key}" is not in the module catalogue.`);
    return definition;
}
function isModuleKey(value) {
    return exports.SWITCHABLE_MODULES.some((key) => key === value);
}
/** One value per switchable module, from one rule. Written out key by key so no cast is needed. */
function perModule(pick) {
    return {
        assessments: pick('assessments'),
        templates: pick('templates'),
        compare: pick('compare'),
        cv_bank: pick('cv_bank'),
        screening_order: pick('screening_order'),
        organisation: pick('organisation'),
        requisitions: pick('requisitions'),
        approvals: pick('approvals'),
        sla: pick('sla'),
        time_metrics: pick('time_metrics'),
        reports: pick('reports'),
        workspace: pick('workspace'),
        career_site: pick('career_site'),
        pools: pick('pools'),
        offers: pick('offers'),
        exits: pick('exits'),
        exam_sessions: pick('exam_sessions'),
        money: pick('money'),
        custom_fields: pick('custom_fields'),
        inbound_mail: pick('inbound_mail'),
        interview_kits: pick('interview_kits'),
        tasks: pick('tasks'),
        screening_criteria: pick('screening_criteria'),
        jev: pick('jev'),
        job_image: pick('job_image'),
        manager_shortlist: pick('manager_shortlist'),
    };
}
function stateOf(pick) {
    return perModule(pick);
}
exports.DEFAULT_MODULE_STATE = stateOf((key) => moduleDefinition(key).isOnByDefault);
/** Every module on — what the app looked like before the reshape. */
exports.ALL_MODULES_ON = stateOf(() => true);
function storedFlag(value, key) {
    if (typeof value !== 'object' || value === null || !(key in value))
        return undefined;
    const raw = Reflect.get(value, key);
    return typeof raw === 'boolean' ? raw : undefined;
}
/**
 * The stored value, read safely. A key that is missing takes its §L34 default, so a module
 * added later ships with its default and needs no migration (§L28); an unknown key is dropped.
 */
function moduleStateOf(value) {
    const flagOf = (key) => {
        const stored = storedFlag(value, key);
        if (stored !== undefined)
            return stored;
        const leader = moduleDefinition(key).comesOnWith;
        return leader === undefined ? moduleDefinition(key).isOnByDefault : flagOf(leader);
    };
    return stateOf(flagOf);
}
function isModuleOn(state, key) {
    return state[key];
}
/**
 * Delegation (cover for an absence, §L40) reroutes hire-request and offer approvals, so it is there
 * while either part is on (review R18, §L34). A cover moves only the approvals whose part is on: a
 * part that is off has no approvals running.
 */
exports.DELEGATION_MODULES = ['requisitions', 'offers'];
function isDelegationOn(state) {
    return exports.DELEGATION_MODULES.some((key) => state[key]);
}
function needsMet(state, definition) {
    if ((definition.needs ?? []).some((need) => !state[need]))
        return false;
    const oneOf = definition.needsOneOf ?? [];
    return oneOf.length === 0 || oneOf.some((need) => state[need]);
}
/** The first rule a state breaks, in words, or null when every module that is on has what it needs. */
function moduleStateProblem(state) {
    for (const definition of exports.MODULE_DEFINITIONS) {
        if (!state[definition.key] || needsMet(state, definition))
            continue;
        return `${definition.label} needs ${needsInWords(definition)}.`;
    }
    return null;
}
function needsInWords(definition) {
    const all = (definition.needs ?? []).map((key) => moduleDefinition(key).label);
    const oneOf = (definition.needsOneOf ?? []).map((key) => moduleDefinition(key).label);
    if (oneOf.length > 0)
        all.push(oneOf.join(' or '));
    return all.join(' and ');
}
/**
 * §L28 dependencies. Turning a module on turns on everything it needs (all of `needs`,
 * transitively); a `needsOneOf` that nothing satisfies blocks, because which one to switch on
 * is the Admin's choice. Turning a module off turns off everything that would be left
 * without what it needs, transitively.
 */
function planModuleChange(state, key, enabled) {
    const next = { ...state };
    if (enabled) {
        const pending = [key];
        while (pending.length > 0) {
            const current = pending.pop();
            if (current === undefined || next[current])
                continue;
            next[current] = true;
            pending.push(...(moduleDefinition(current).needs ?? []));
            pending.push(...exports.MODULE_DEFINITIONS.filter((definition) => definition.comesOnWith === current).map((definition) => definition.key));
        }
        for (const definition of exports.MODULE_DEFINITIONS) {
            if (next[definition.key] && !needsMet(next, definition)) {
                return { next: state, alsoChanged: [], blockedBy: `Turn on ${needsInWords(definition)} first: ${definition.label} needs one of them.`, choices: definition.needsOneOf ?? [] };
            }
        }
    }
    else {
        next[key] = false;
        let changed = true;
        while (changed) {
            changed = false;
            for (const definition of exports.MODULE_DEFINITIONS) {
                if (next[definition.key] && !needsMet(next, definition)) {
                    next[definition.key] = false;
                    changed = true;
                }
            }
        }
    }
    const alsoChanged = exports.SWITCHABLE_MODULES.filter((module) => module !== key && next[module] !== state[module]);
    return { next, alsoChanged, blockedBy: null, choices: [] };
}
/**
 * The answer to a blocked switch-on: `choice` (one of `key`'s "needs one of", with everything it needs) goes on, then
 * `key`. `alsoChanged` is measured from the state before either, so the question names every switch that moves.
 */
function planSwitchOnWith(state, key, choice) {
    const first = planModuleChange(state, choice, true);
    if (first.blockedBy)
        return first;
    const second = planModuleChange(first.next, key, true);
    if (second.blockedBy)
        return second;
    const alsoChanged = exports.SWITCHABLE_MODULES.filter((module) => module !== key && second.next[module] !== state[module]);
    return { next: second.next, alsoChanged, blockedBy: null, choices: [] };
}
/** Every module whose `needs`, `needsOneOf` or `comesOnWith` names this one, in ADMIN-17's order. */
function moduleDependents(key) {
    return exports.MODULE_DEFINITIONS.flatMap((definition) => {
        if (definition.needs?.includes(key))
            return [{ module: definition.key, kind: 'needs' }];
        if (definition.needsOneOf?.includes(key))
            return [{ module: definition.key, kind: 'needs_one_of' }];
        if (definition.comesOnWith === key)
            return [{ module: definition.key, kind: 'comes_on_with' }];
        return [];
    });
}
/**
 * ADMIN-17's Undo. Puts back only what one change moved, on top of the state as it is now, so a switch flipped in the
 * meantime stays as it is. Modules already back where they were are left out.
 */
function planRevert(state, previous) {
    const next = { ...state };
    const changes = previous.filter((change) => state[change.module] !== change.enabled);
    for (const change of changes)
        next[change.module] = change.enabled;
    return { changes, next, problem: changes.length === 0 ? null : moduleStateProblem(next) };
}
// --- ADMIN-17 requests ------------------------------------------------------------------------------
exports.saveModulesSchema = zod_1.z.object({
    /** The version of the `modules` setting the Admin was looking at (S7 stale-write rule). */
    version: zod_1.z.number().int().min(0),
    changes: zod_1.z
        .array(zod_1.z.object({ module: zod_1.z.enum(exports.SWITCHABLE_MODULES), enabled: zod_1.z.boolean() }))
        .min(1)
        .max(exports.SWITCHABLE_MODULES.length),
});
exports.MODULE_EVENTS = {
    TOGGLED: 'settings.module_toggled',
};
// --- What each module owns outside its screens (§L28) ----------------------------------------------
/**
 * The notifications that belong to a switchable module. One that belongs to a module that is
 * off is never created (§L28, §L34). Everything not listed is core and always goes. A list means
 * any of them: the notification goes while one of them is on.
 */
exports.NOTIFICATION_MODULES = {
    'requisition.approval_requested': 'requisitions',
    'requisition.approval_pending': 'requisitions',
    'requisition.approval_overdue': 'requisitions',
    'requisition.approved': 'requisitions',
    'requisition.rejected': 'requisitions',
    'requisition.sent_back': 'requisitions',
    'requisition.on_hold': 'requisitions',
    'requisition.no_job': 'requisitions',
    'requisition.job_ready': 'requisitions',
    // Delegation moves hire-request and offer approvals, so it lives with either (review R18).
    'delegation.active': exports.DELEGATION_MODULES,
    'sla.escalated': 'sla',
    'sla.escalated_application': 'sla',
    'screening.overdue': 'sla',
    'sla.breach_summary': 'sla',
    'posting.expiring': 'career_site',
    'screening.auto_reject_rate': 'career_site',
    'assessment.assigned': 'assessments',
    'assessment.reminder': 'assessments',
    'assessment.expired': 'assessments',
    'assessment.submitted': 'assessments',
    'assessment.grading_pending': 'assessments',
    'assessment.grading_overdue': 'assessments',
    'exam_session.tomorrow': 'exam_sessions',
    'hold.expiring': 'compare',
    'hold.silver_available': 'compare',
    'task.due': 'tasks',
    'task.overdue': 'tasks',
    'task.answered': 'tasks',
    'message.reply_application': 'inbound_mail',
    'message.reply_candidate': 'inbound_mail',
    'pool.strong_match': 'pools',
    'pool.consent_expiring': 'pools',
    'offer.approval_requested': 'offers',
    'offer.approval_pending': 'offers',
    'offer.approved': 'offers',
    'offer.approval_rejected': 'offers',
    'offer.expiring': 'offers',
    'offer.viewed': 'offers',
    'offer.accepted': 'offers',
    'offer.declined': 'offers',
    'offer.expired': 'offers',
    'offer.no_show': 'offers',
    'hire.joining_soon': 'offers',
    'hire.joining_unconfirmed': 'offers',
    'hire.documents_outstanding': 'offers',
    'report.morning_digest': 'reports',
    'report.scheduled': 'reports',
    'workspace.digest': 'workspace',
    'shortlist.ready': 'manager_shortlist',
    'shortlist.added': 'manager_shortlist',
    'shortlist.reminder': 'manager_shortlist',
    'shortlist.finished': 'manager_shortlist',
};
/** Whether a notification may be created under this state (§L28). */
function isNotificationOn(state, notificationKey) {
    const module = exports.NOTIFICATION_MODULES[notificationKey];
    return module === undefined || isAnyOn(state, module);
}
function isAnyOn(state, module) {
    return typeof module === 'string' ? state[module] : module.some((key) => state[key]);
}
/**
 * The notification keys whose module is off. The inbox and the bell leave these out
 * rather than deleting them, so they come back when the module is switched on (§L28).
 */
function notificationKeysOff(state) {
    return Object.entries(exports.NOTIFICATION_MODULES)
        .filter(([, module]) => !isAnyOn(state, module))
        .map(([key]) => key);
}
//# sourceMappingURL=modules.js.map